import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { fallbackProductHandle } from '../data/product-fetch-cache';

export interface QueryParams {
  productHandle: string;
  selectedFoamPackage: string;
  sessionUuid: string | null;
  variantId: string | null;
  reorderLeftFootLengthInMM: string | null;
  reorderRightFootLengthInMM: string | null;
  reorderLeftFootArchHeightInMM: string | null;
  reorderRightFootArchHeightInMM: string | null;
  reorderLeftEngraving: string | null;
  reorderRightEngraving: string | null;
  reorderFirstNameOnBox: string | null;
  reorderLastNameOnBox: string | null;
}

/**
 * Hook to extract query parameters from URL
 * This is used when the app is embedded in a Shopify store or when loading an existing session
 *
 * With HashRouter, URLs look like: /#/route?param=value
 * Also checks main URL params for Shopify embed case: /pages/insole?param=value#/
 *
 * Query parameters:
 * - product_handle: Shopify product handle
 * - selected_foam_package: Pre-selected foam package option
 * - session_uuid: UUID of existing Firebase session to load (removed after reading)
 * - left_foot_length_mm / right_foot_length_mm: saved foot length (mm), for reordering
 * - left_foot_arch_height_mm / right_foot_arch_height_mm: saved arch height (mm), for reordering
 * - left_engraving / right_engraving: saved heel engraving text, for reordering
 * - first_name_on_box / last_name_on_box: saved box name text, for reordering
 */

const QUERY_PARAM_KEYS_TO_REMOVE = [
  'session_uuid',
  'left_foot_length_mm',
  'right_foot_length_mm',
  'left_foot_arch_height_mm',
  'right_foot_arch_height_mm',
  'left_engraving',
  'right_engraving',
  'first_name_on_box',
  'last_name_on_box',
  'session_uuid',
];

export const useQueryParams = (): QueryParams => {
  const [searchParams, setSearchParams] = useSearchParams();

  const productHandle = searchParams.get('product_handle') || fallbackProductHandle || "";
  const selectedFoamPackage = searchParams.get('selected_foam_package') || "";
  const sessionUuid = searchParams.get('session_uuid');
  const variantId = searchParams.get('variant_id') || "";
  const reorderLeftFootLengthInMM = searchParams.get('left_foot_length_mm');
  const reorderRightFootLengthInMM = searchParams.get('right_foot_length_mm');
  const reorderLeftFootArchHeightInMM = searchParams.get('left_foot_arch_height_mm');
  const reorderRightFootArchHeightInMM = searchParams.get('right_foot_arch_height_mm');
  const reorderLeftEngraving = searchParams.get('left_engraving');
  const reorderRightEngraving = searchParams.get('right_engraving');
  const reorderFirstNameOnBox = searchParams.get('first_name_on_box');
  const reorderLastNameOnBox = searchParams.get('last_name_on_box');

  // Remove session_uuid and the reorder params from the URL once they've
  // been read — AppContext's lazy initializer already consumes them
  // synchronously during the first render (before this effect runs), so
  // stripping them here doesn't affect the values that get seeded. Keeps
  // them from lingering if the page is bookmarked/shared/reloaded.
  useEffect(() => {
    const hasParamsToRemove = QUERY_PARAM_KEYS_TO_REMOVE.some((key) => searchParams.has(key));
    if (hasParamsToRemove) {
      const newParams = new URLSearchParams(searchParams);
      QUERY_PARAM_KEYS_TO_REMOVE.forEach((key) => newParams.delete(key));
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  return {
    productHandle,
    selectedFoamPackage,
    sessionUuid,
    variantId,
    reorderLeftFootLengthInMM,
    reorderRightFootLengthInMM,
    reorderLeftFootArchHeightInMM,
    reorderRightFootArchHeightInMM,
    reorderLeftEngraving,
    reorderRightEngraving,
    reorderFirstNameOnBox,
    reorderLastNameOnBox,
  };
};

